import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subscription, combineLatest, from, merge } from 'rxjs';
import {
  CreateSurveyService,
  MultiCountryData,
} from '../../../create-survey.service';
import {
  MultiCountryPreDraftDataMapper,
  SurveyRequestMapper,
} from './domain/multi-country-pre-draft-data-mapper';
import { MultiCountryHeaderCompletesMapper } from './domain/multi-country-common-data-mapper';
import { MultiCountrySurveyMapper } from './domain/multi-country-traditional-view-data-mapper';
import {
  debounceTime,
  filter,
  map,
  mergeMap,
  share,
  skip,
  startWith,
  switchMap,
  tap,
  toArray,
} from 'rxjs/operators';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import {
  FeasibilityPayload,
  FeasibilityResponse,
} from '../../../../shared/interfaces/feasibility.interface';
import { LocaleSorter } from './domain/locale-sorter';
import { CountryInterface } from '../../../../shared/interfaces/create-surveys.interface';
import { BuyerSurveyResponse } from '../../../../shared/interfaces/survey.interface';
import { Router } from '@angular/router';
import {
  McSurveyRequest,
  McPreDraftSurvey,
  MultiCountrySurvey,
  MultiCountrySpvSurvey,
  McSpvSurveyRequest,
} from './interface/multi-country.interface';
import { MultiCountryDraftService } from '../../../services/multi-country-draft/multi-country-draft.service';
import { MultiCountryService } from './services/multi-country.service';
import { MultiCountrySpvSurveyMapper } from './domain/multi-country-single-view-data-mapper';
import { SurveyPayload } from '../../../../shared/interfaces/survey-meta-data.interface';
import { LocaleUpdater } from './domain/locale-updater';

@Component({
  selector: 'ps-ui-multi-country',
  templateUrl: './multi-country.component.html',
  styleUrls: ['./multi-country.component.css'],
})
export class MultiCountryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() surveyDetails = <BuyerSurveyResponse>{};
  @Input() surveyPayload = <SurveyPayload>{};
  @Input() countries: CountryInterface[] = [];
  @Input() selectedTemplateId?: number | null;

  @Output() whenMcDataPresent = new EventEmitter<boolean>();
  @Output() mcSurveyAdded = new EventEmitter();
  @Output() selectionChange = new EventEmitter();
  @Output() cancelTemplateSelection = new EventEmitter();

  private _subscriptions: Subscription = new Subscription();
  private _surveyDetailsCommand$ = new EventEmitter<BuyerSurveyResponse>();
  private _mcPreDraftDataCommand$ = new EventEmitter<McPreDraftSurvey[]>();
  private _mcSurveyDataCommand$ = new EventEmitter<MultiCountrySurvey[]>();
  private _mcSpvChildSurveysCommand$ = new EventEmitter<
    MultiCountrySpvSurvey[]
  >();
  private _userType = this._auth.userType;

  public multiCountryPreDraftData: McPreDraftSurvey[] = [];
  public selectedMultiCountryPreDraftData: McPreDraftSurvey[] = [];
  public multiCountrySurveyData: MultiCountrySurvey[] = [];
  public isMultiCountryEnabled: boolean = false;
  public isMultiCountrySingleViewEnabled: boolean = false;
  public completesRequired: number = 0;
  public multiCountrySpvParentSurvey = <BuyerSurveyResponse>{};
  public multiCountrySpvChildSurveys: MultiCountrySpvSurvey[] = [];
  public mcSvpPayload = <McSpvSurveyRequest>{};

  public mcPreDraftDataStream$!: Observable<MultiCountryData>;
  public mcPreDraftDataPresent$!: Observable<boolean>;
  public mcSurveyDataPresent$!: Observable<boolean>;

  constructor(
    private _auth: AuthService,
    private _createSurveyService: CreateSurveyService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _multiCountryService: MultiCountryService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this._initiateBuyerConfigFlags();
    this._initiateDataStreams();
    this._decideIfMcDataPresent();
    this._getMultiCountryPreDraftData();
    this._handleSurveyDetailsChanges();
    this._handleMcPreDraftDataChanges();
    this._handleMcSurveyDataChanges();
    this._handleMcSpvSurveyDataChanges();
    this._handleMcSvpPayload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.surveyDetails?.currentValue) {
      this._surveyDetailsCommand$.next(this.surveyDetails);
    }
  }

  private _initiateBuyerConfigFlags() {
    this.isMultiCountryEnabled = !!this._auth.buyerConfig?.enableMultiCountry;
    this.isMultiCountrySingleViewEnabled =
      !!this._auth.buyerConfig?.enableMultiCountrySingleProjectView ||
      this._userType === 'operator';
  }

  private _initiateDataStreams() {
    this.mcPreDraftDataStream$ = this._createSurveyService
      .getMultiCountryData()
      .pipe(
        filter(
          () =>
            this.isMultiCountryEnabled || this.isMultiCountrySingleViewEnabled
        ),
        share()
      );
  }

  private _decideIfMcDataPresent() {
    this.mcPreDraftDataPresent$ = this.mcPreDraftDataStream$.pipe(
      filter((data) => !data.surveyId),
      map((data) => this._checkIMcfPreDraftDataPresent(data)),
      startWith(false)
    );

    this.mcSurveyDataPresent$ = merge(
      this._mcSurveyDataCommand$,
      this._mcSpvChildSurveysCommand$
    ).pipe(
      map((data) => this._checkIfMcSurveyDataPresent(data)),
      startWith(false)
    );

    this._subscriptions.add(
      combineLatest([
        this.mcPreDraftDataPresent$,
        this.mcSurveyDataPresent$,
      ]).subscribe(([mcPreDraftDataPresent, mcSurveyDataPresent]) => {
        const isPresent = mcPreDraftDataPresent || mcSurveyDataPresent;
        if (!isPresent) {
          this._multiCountryDraftService.setMultiCountryPreDraftData([]);
          this._createSurveyService.setMultiCountryTotalCompletesDefaultValue();
        }
        this.whenMcDataPresent.emit(isPresent);
      })
    );
  }

  private _checkIMcfPreDraftDataPresent(data: MultiCountryData) {
    return (
      data.isDraft &&
      (data.selectedCountries.length > 1 || data.selectedLanguages.length > 1)
    );
  }

  private _checkIfMcSurveyDataPresent(data: MultiCountrySurvey[]) {
    return !!(data && data.length);
  }

  private _getMultiCountryPreDraftData() {
    const selectedCountiresDataChange$ = this.mcPreDraftDataStream$.pipe(
      filter((data) => !!data.isDraft),
      tap(() => (this.multiCountryPreDraftData = [])),
      debounceTime(1000)
    );

    const mcSurveyRquests$ = selectedCountiresDataChange$.pipe(
      tap((data) => {
        this.completesRequired = data.completesRequired;
      }),
      map((data) => {
        const buyerId = this._auth.isLoggedInAsServiceBuyer()
          ? this._auth.companyConfig.id
          : this._auth.user!.cmp;
        const userId = this._auth.user!.id;
        return <[MultiCountryData, number, number]>[data, buyerId, userId];
      }),
      map(([data, buyerId, userId]) =>
        new SurveyRequestMapper(
          data,
          buyerId,
          userId,
          this._auth.isLoggedInAsServiceBuyer()
        ).transform()
      ),
      map((datas) => {
        return datas.map((data) => {
          const selectPreDeaftData = this.selectedMultiCountryPreDraftData.find(
            (preDraftData) =>
              preDraftData.survey_localization === data.survey_localization
          );
          return {
            ...data,
            checked: selectPreDeaftData ? selectPreDeaftData.checked : true,
          };
        });
      })
    );

    const payloads$ = mcSurveyRquests$.pipe(
      switchMap((surveyRequests) => surveyRequests),
      map(
        (surveyRequest) =>
          <[McSurveyRequest, FeasibilityPayload]>[
            surveyRequest,
            this._multiCountryService.buildFeasibilityPayloadForPreDraft(
              surveyRequest
            ),
          ]
      )
    );

    this._subscriptions.add(
      payloads$
        .pipe(
          mergeMap(([surveyRequest, { request: feasibilityRequest }]) =>
            this._multiCountryService.getFeasibility(feasibilityRequest).pipe(
              filter(
                (
                  feasibilityResponse
                ): feasibilityResponse is FeasibilityResponse =>
                  !!feasibilityResponse
              ),
              map((feasibilityResponse) =>
                new MultiCountryPreDraftDataMapper(
                  surveyRequest,
                  feasibilityResponse,
                  this._auth.isLoggedInAsServiceBuyer()
                ).transform()
              )
            )
          ),
          map((upcomingPreDraftSurvey) => {
            const updated = new LocaleUpdater(
              this.multiCountryPreDraftData,
              upcomingPreDraftSurvey
            ).update();
            return new LocaleSorter(updated).sort();
          })
        )
        .subscribe((mcPreDraftData) => {
          this.multiCountryPreDraftData = mcPreDraftData;
          this._mcPreDraftDataCommand$.next(mcPreDraftData);
        })
    );
  }

  private _handleSurveyDetailsChanges() {
    const traditionalMcStream$ = this._surveyDetailsCommand$.pipe(
      filter(
        () => this.isMultiCountryEnabled || this._auth.userType === 'operator'
      ),
      filter((surveyData) => !!surveyData.mc_unique_id),
      filter((surveyData) => !surveyData.mc.SVP),
      switchMap((surveyData) =>
        this._multiCountryService.getSurveys(surveyData)
      )
    );

    const mcSpvStream$ = this._surveyDetailsCommand$.pipe(
      filter(
        () =>
          this.isMultiCountrySingleViewEnabled ||
          this._auth.userType === 'operator'
      ),
      filter((surveyData) => !!surveyData.mc_unique_id),
      filter((surveyData) => !!surveyData.mc.SVP),
      switchMap((surveyData) =>
        this._multiCountryService.getSurveys(surveyData)
      ),
      map((surveys) => {
        // we can move this to a seperate function/transformer/mapper if in future we need to manipulate other SVP survey data.
        const parentSpvSurvey = surveys.find((survey) => survey.mc.parent);
        surveys.map((survey) => {
          if (!survey.mc.parent) {
            survey.survey_grouping = parentSpvSurvey?.survey_grouping ?? [];
          }
          return survey;
        });
        return surveys;
      }),
      tap((data) =>
        this._multiCountryDraftService.sendSpvSurveyResponseData(data)
      ),
      share()
    );

    const parentSpvSurveyStream$ = mcSpvStream$.pipe(
      switchMap((data) =>
        from(data).pipe(
          filter((survey) => !!survey.mc.parent),
          toArray()
        )
      ),
      map(([survey]) => survey)
    );

    const childSpvSurveysStream$ = mcSpvStream$.pipe(
      switchMap((data) =>
        from(data).pipe(
          filter((survey) => !survey.mc.parent),
          toArray()
        )
      )
    );

    this._subscriptions.add(
      traditionalMcStream$.subscribe((surveys) => {
        const newSurveys = surveys.filter(
          (survey) =>
            !this.multiCountrySurveyData.some(
              (existing) => existing.survey_id === survey.ps_survey_id
            )
        );
        const mappedNewMcSurveys = new MultiCountrySurveyMapper(
          newSurveys,
          this.surveyDetails.mc_unique_id!,
          this._auth.isLoggedInAsServiceBuyer()
        ).transform();
        const newSurveysIds = mappedNewMcSurveys.map(
          (mcSurvey) => mcSurvey.survey_id
        );
        this._setUniqueBuyerlinks(surveys);
        this._mcSurveyDataCommand$.next([
          ...this.multiCountrySurveyData,
          ...mappedNewMcSurveys,
        ]);
        this._multiCountryDraftService.sendMcTraditionalSurveysResponse(
          surveys
        );
        this._multiCountryDraftService.setMultiCountryRunFeasibilityInfo({
          shouldSetFeasibilityFromFlyout: true,
          runFeasCalcForSurveys: newSurveysIds,
        });
      })
    );

    this._subscriptions.add(
      parentSpvSurveyStream$.subscribe((survey) => {
        this.multiCountrySpvParentSurvey = survey;
      })
    );

    this._subscriptions.add(
      childSpvSurveysStream$.subscribe((childSurveys) => {
        const newChildSurveys = childSurveys.filter(
          (childSurvey) =>
            !this.multiCountrySpvChildSurveys.some(
              (existing) => existing.survey_id === childSurvey.ps_survey_id
            )
        );
        const mappedNewChildSurveys = new MultiCountrySpvSurveyMapper(
          newChildSurveys,
          this.surveyDetails.mc_unique_id!
        ).transform();
        this._mcSpvChildSurveysCommand$.next([
          ...this.multiCountrySpvChildSurveys,
          ...mappedNewChildSurveys,
        ]);
      })
    );
  }

  private _handleMcPreDraftDataChanges(): void {
    this._subscriptions.add(
      this._mcPreDraftDataCommand$.subscribe((data) => {
        this.selectedMultiCountryPreDraftData = data;
        this._multiCountryDraftService.setMultiCountryPreDraftData(data);
      })
    );

    this._subscriptions.add(
      this._mcPreDraftDataCommand$
        .pipe(
          map((data) => new MultiCountryHeaderCompletesMapper(data).transform())
        )
        .subscribe((headerCompletesData) => {
          this._createSurveyService.setMultiCountryTotalCompletes(
            headerCompletesData
          );
        })
    );
  }

  public onMultiCountryPreDraftDataUpdate(data: McPreDraftSurvey[]): void {
    this._mcPreDraftDataCommand$.next(data);
  }

  private _handleMcSurveyDataChanges(): void {
    this._subscriptions.add(
      this._mcSurveyDataCommand$
        .pipe(
          map((data: MultiCountrySurvey[]) => new LocaleSorter(data).sort())
        )
        .subscribe((data) => {
          // When no data, redirect to dashboard
          if (!data.length) {
            this._router.navigateByUrl('/dashboard');
          }
          this.multiCountrySurveyData = data;
          this._multiCountryDraftService.setMultiCountrySurveyData(data);
          this._createSurveyService.setMultiCountryTotalCompletes(
            new MultiCountryHeaderCompletesMapper(data).transform()
          );
        })
    );
  }

  private _handleMcSpvSurveyDataChanges(): void {
    this._subscriptions.add(
      this._mcSpvChildSurveysCommand$
        .pipe(map((childSurveys) => new LocaleSorter(childSurveys).sort()))
        .subscribe((surveys) => {
          this.multiCountrySpvChildSurveys = surveys;
          this._multiCountryDraftService.sendMcSpvChildSurveys(surveys);
        })
    );
  }

  private _handleMcSvpPayload(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcSpvSurveyRequest()
        .pipe(skip(1))
        .subscribe((data) => {
          this.mcSvpPayload = data;
          this._createSurveyService.setMultiCountryTotalCompletes(
            new MultiCountryHeaderCompletesMapper(data.surveys).transform()
          );
        })
    );
  }

  public onMultiCountrySurveyDataUpdate(data: MultiCountrySurvey[]): void {
    this._mcSurveyDataCommand$.next(data);
  }

  public onMcSpvSurveyDataUpdate(data: MultiCountrySpvSurvey[]): void {
    this._mcSpvChildSurveysCommand$.next(data);
  }

  public onMcSurveyAdded(surveyData?: BuyerSurveyResponse) {
    this.mcSurveyAdded.emit(surveyData);
  }

  public _updateClickBalancing(data: MultiCountrySpvSurvey[]) {
    return data.map((survey) => {
      return {
        ...survey,
        ...{ click_balancing: this._getClickBalancing(survey) },
      };
    });
  }

  private _getClickBalancing(survey: MultiCountrySpvSurvey) {
    return this.mcSvpPayload.surveys.find(
      (mcSvpSurvey) => mcSvpSurvey.survey_id === survey.survey_id
    )?.click_balancing;
  }

  private _setUniqueBuyerlinks(surveys: BuyerSurveyResponse[]): void {
    this.multiCountrySurveyData.forEach((mcSurvey: MultiCountrySurvey) => {
      const responseSurvey = surveys.find(
        (survey) => survey.ps_survey_id === mcSurvey.survey_id
      );
      if (responseSurvey) {
        mcSurvey.unique_links = responseSurvey.unique_links;
      }
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
